import React from 'react';
import {
  formatDate,
  formatDateTime,
  toDisplayPrimitive,
} from '../../utils/formatUtils';

/** Shared helpers for global search palette + results UI */

const DATETIME_FIELD_NAMES = new Set([
  'createddate',
  'lastmodifieddate',
  'closedate',
  'activitydate',
  'duedate',
  'startdatetime',
  'enddatetime',
]);

const DATE_FIELD_NAMES = new Set([
  'birthdate',
  'startdate',
  'enddate',
  'closedate',
]);

/** Detect JS Date#toString / ISO / common SQL timestamps */
function looksLikeDateTimeString(value) {
  const s = String(value || '').trim();
  if (!s) return false;
  if (/^\d{4}-\d{2}-\d{2}([T\s]\d{2}:\d{2})/.test(s)) return true;
  if (/^[A-Za-z]{3}\s+[A-Za-z]{3}\s+\d{1,2}\s+\d{4}\s+\d{2}:\d{2}:\d{2}\s+GMT/.test(s)) {
    return true;
  }
  return false;
}

function looksLikeDateOnlyString(value) {
  const s = String(value || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

/**
 * Format a primitive / date-like value (same date rules as list views).
 */
export function formatSearchCellValue(value, dataType, fieldName) {
  if (value == null || value === '') return '';
  const dt = String(dataType || '').toLowerCase().trim();
  const fname = String(fieldName || '').toLowerCase().trim();

  const asDateTime =
    dt === 'datetime' ||
    dt === 'datetime2' ||
    dt === 'timestamp' ||
    DATETIME_FIELD_NAMES.has(fname) ||
    looksLikeDateTimeString(value);
  const asDate =
    dt === 'date' ||
    (DATE_FIELD_NAMES.has(fname) && !asDateTime) ||
    looksLikeDateOnlyString(value);

  if (asDate && !asDateTime) {
    const formatted = formatDate(value);
    return formatted || String(value);
  }
  if (asDateTime) {
    const formatted = formatDateTime(value);
    return formatted || String(value);
  }
  if (dt === 'boolean' || dt === 'checkbox') {
    if (value === true || value === 'true' || value === 1 || value === '1') {
      return 'Yes';
    }
    if (value === false || value === 'false' || value === 0 || value === '0') {
      return 'No';
    }
  }
  const primitive = toDisplayPrimitive(value);
  if (primitive == null || primitive === '') return '';
  return String(primitive);
}

/**
 * Same display preference as ResizableTable.formatFieldValue:
 * 1) displayValue (lookups / picklists from fetchRecords)
 * 2) type-based formatting on value (date/datetime/boolean)
 * 3) raw value
 */
export function getSearchFieldDisplayText(field, dataType, fieldName) {
  if (field == null || field === '') return '';

  // Legacy flat string from older API responses
  if (typeof field !== 'object') {
    return formatSearchCellValue(field, dataType, fieldName);
  }

  // ResizableTable: prefer displayValue for lookup / picklist / uuid labels
  if (
    field.displayValue !== undefined &&
    field.displayValue !== null &&
    field.displayValue !== ''
  ) {
    const safe = toDisplayPrimitive(field.displayValue);
    if (safe !== null && safe !== undefined && safe !== '') {
      return String(safe);
    }
  }

  const type = String(field.type || dataType || '').toLowerCase().trim();
  const fname = String(fieldName || '').toLowerCase().trim();
  return formatSearchCellValue(field.value, type || dataType, fname || fieldName);
}

/** True when this cell should link to the related lookup record (ListView behavior). */
export function isSearchLookupField(field) {
  if (!field || typeof field !== 'object') return false;
  if (!field.isReference) return false;
  if (field.value == null || field.value === '') return false;
  if (!field.referenceObject) return false;
  const label = field.displayValue ?? field.value;
  return label != null && String(label).trim() !== '';
}

export function initials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (parts.length === 0) return '?';
  return parts.map((w) => w[0]).join('').toUpperCase();
}

export function objectTone(objectName) {
  const n = String(objectName || '').toLowerCase();
  if (n === 'lead') return 'lead';
  if (n === 'account') return 'account';
  if (n === 'contact') return 'contact';
  if (n === 'opportunity') return 'opportunity';
  const tones = ['lead', 'account', 'contact', 'opportunity'];
  let h = 0;
  for (let i = 0; i < n.length; i += 1) h = (h + n.charCodeAt(i)) % tones.length;
  return tones[h] || 'muted';
}

export function highlightText(text, query) {
  const value = text == null ? '' : String(text);
  const needle = String(query || '').trim();
  if (!needle) return value;
  const idx = value.toLowerCase().indexOf(needle.toLowerCase());
  if (idx < 0) return value;
  return (
    <>
      {value.slice(0, idx)}
      <mark>{value.slice(idx, idx + needle.length)}</mark>
      {value.slice(idx + needle.length)}
    </>
  );
}
